import React, { useState, useEffect } from "react";
import {
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  useMediaQuery,
  useTheme,
  Link
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useCreatePlanSubscriptionMutation } from "../../store/userApi";
import { getErrorMessage } from "../../error-helpers/errorMessage";
import { showSuccessToast } from "../../toastUtils";
import { useClerk } from "@clerk/clerk-react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

// Lista de países
const countries = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
  { value: "UK", label: "United Kingdom" },
  { value: "ES", label: "Spain" },
];

// Estados de EE.UU.
const usStates = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

interface SubscriptionFormDirectProps {
  onCancel: () => void;
  onSuccess: () => void;
  planId?: string;
  planPrice?: number;
  trialPeriodDays?: number;
  isDirectSignup?: boolean;
}

const SubscriptionFormDirect: React.FC<SubscriptionFormDirectProps> = ({ 
  onCancel, 
  onSuccess,
  planId = "pro_plan",
  planPrice = 9.99,
  trialPeriodDays = 14,
  isDirectSignup = false
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const clerk = useClerk();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createPlanSubscription] = useCreatePlanSubscriptionMutation();
  
  // Get user state from Redux store
  const user = useSelector((state: RootState) => state.user.user);
  
  // Determine if user is eligible for trial period - must be both a direct signup AND not had a subscription before
  // If coming from DirectProSignupPage, we give trial regardless of hadSubscriptionBefore status
  const isEligibleForTrial = isDirectSignup || (!user?.hadSubscriptionBefore && trialPeriodDays > 0);
  
  // Check for mobile devices
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estados para el formulario
  const [country, setCountry] = useState("US");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setErrorMessage("Stripe has not been initialized correctly");
      return;
    }

    // Verify user is authenticated
    if (!clerk.session?.id) {
      setErrorMessage("You must be logged in to complete this purchase. Please refresh the page.");
      return;
    }

    // Verificar los términos y condiciones
    if (!agreedToTerms) {
      setErrorMessage("Please agree to the terms and conditions");
      return;
    }

    // Only validate zipCode as required
    if (!zipCode.trim()) {
      setErrorMessage("Postal code is required");
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      // Obtener la referencia al elemento CardNumber
      const cardElement = elements.getElement(CardNumberElement);
      
      if (!cardElement) {
        setErrorMessage("Card element not found. Please refresh and try again.");
        setLoading(false);
        return;
      }

      // Log current user session for debugging
      console.log("Creating subscription with active session:", clerk.session.id);
      console.log("Current user:", clerk.user?.id);

      // Crear método de pago
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          address: {
            line1: street || undefined,
            city: city || undefined,
            country,
            state: state || undefined,
            postal_code: zipCode,
          },
          // Add email from current user to make linking more reliable
          email: clerk.user?.primaryEmailAddress?.emailAddress || undefined,
          name: `${clerk.user?.firstName || ''} ${clerk.user?.lastName || ''}`.trim() || undefined
        },
      });

      if (error) {
        setErrorMessage(error.message || "An error occurred with your payment");
        setLoading(false);
        return;
      }

      // Crear suscripción y añadir período de prueba según la documentación de Stripe
      console.log('Creating subscription with trial period days:', isEligibleForTrial ? trialPeriodDays : 0);
      
      // Prepare subscription data object
      const subscriptionData: any = {
        trial_from_plan: false,
        isDirectSignup: isDirectSignup
      };
      
      // Only add trial period if user is eligible
      if (isEligibleForTrial) {
        subscriptionData.trial_period_days = trialPeriodDays;
      }
      
      const result = await createPlanSubscription({
        paymentMethodId: paymentMethod.id,
        planId: planId,
        customerName: {
          firstName: clerk.user?.firstName || '',
          lastName: clerk.user?.lastName || ''
        },
        subscription_data: subscriptionData,
        billingAddress: {
          line1: street || "",
          line2: "",
          city: city || "",
          state: state || "",
          postal_code: zipCode,
          country: country
        }
      }).unwrap();

      console.log('Subscription created successfully', result);
      showSuccessToast('Subscription created successfully!');
      
      // Touch the session to ensure it's active before moving on
      try {
        await clerk.session?.touch();
      } catch (err) {
        console.error("Error touching session", err);
        // Continue anyway, as it's not critical
      }
      
      // Call onSuccess immediately to ensure the redirect happens
      // We're already in an async context so this is safe
      if (onSuccess) {
        console.log("Calling onSuccess callback to proceed with redirect");
        onSuccess();
      }
      setLoading(false);
    } catch (error) {
      console.error("Error during subscription creation:", error);
      setErrorMessage(getErrorMessage(error) || "Failed to create subscription");
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        '::placeholder': {
          color: 'rgba(255, 255, 255, 0.6)',
        },
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle sx={{ px: 0, color: '#FFFFFF' }}>
        Subscribe to Pro Plan
      </DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
          Unlock unlimited transcriptions and all premium features for ${planPrice}/month.
          {isEligibleForTrial && (
            <Box component="span" sx={{ 
              display: 'flex', 
              color: '#4ade80', 
              fontWeight: 'bold', 
              mt: 1,
              fontSize: '0.9rem',
              alignItems: 'center'
            }}>
              <AccessTimeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              Your subscription includes a {trialPeriodDays}-day free trial
            </Box>
          )}
        </DialogContentText>
        
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#FFFFFF', fontWeight: 500 }}>
          Card Information
        </Typography>
        
        <Box
          sx={{
            border: '1px solid',
            borderColor: errorMessage ? '#ef4444' : 'rgba(255, 255, 255, 0.2)',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          <CardNumberElement options={cardElementOptions} />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={isMobile ? 12 : 6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                p: 2,
                mb: isMobile ? 2 : 0,
              }}
            >
              <CardExpiryElement options={cardElementOptions} />
            </Box>
          </Grid>
          <Grid item xs={isMobile ? 12 : 6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                p: 2,
              }}
            >
              <CardCvcElement options={cardElementOptions} />
            </Box>
          </Grid>
        </Grid>

        <Typography variant="subtitle1" gutterBottom sx={{ color: '#FFFFFF', fontWeight: 500, mt: 2 }}>
          Billing Address
        </Typography>

        <Grid container spacing={2}>
          {/* Street and City on the same line (optional) - responsive for mobile */}
          <Grid item xs={isMobile ? 12 : 6}>
            <TextField
              label="Street Address (Optional)"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              placeholder="123 Main St"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputBase-input': {
                  color: '#FFFFFF',
                },
              }}
            />
          </Grid>
          
          <Grid item xs={isMobile ? 12 : 6}>
            <TextField
              label="City (Optional)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputBase-input': {
                  color: '#FFFFFF',
                },
              }}
            />
          </Grid>
          
          {/* State and ZIP before country - responsive for mobile */}
          {country === 'US' && (
            <Grid item xs={isMobile ? 12 : 6}>
              <TextField
                select
                label="State"
                value={state}
                onChange={(e) => setState(e.target.value)}
                fullWidth
                variant="outlined"
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiSelect-select': {
                    color: '#FFFFFF',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              >
                {usStates.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          
          <Grid item xs={country === 'US' && !isMobile ? 6 : 12}>
            <TextField
              label="ZIP / Postal Code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputBase-input': {
                  color: '#FFFFFF',
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              select
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiSelect-select': {
                  color: '#FFFFFF',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            >
              {countries.map((country) => (
                <MenuItem key={country.value} value={country.value}>
                  {country.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-checked': {
                  color: '#3b82f6',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              I agree to the{' '}
              <Link component={RouterLink} to="/terms-of-service" target="_blank" sx={{ color: '#3b82f6' }}>
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link component={RouterLink} to="/privacy-policy" target="_blank" sx={{ color: '#3b82f6' }}>
                Privacy Policy
              </Link>
            </Typography>
          }
          sx={{
            mt: 2,
          }}
        />

        {errorMessage && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <LockOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Your payment is secured with SSL encryption
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        padding: "16px 0", 
        borderTop: "1px solid rgba(255,255,255,0.1)",
        justifyContent: "space-between" 
      }}>
        <Button 
          onClick={onCancel}
          sx={{ color: "rgba(255,255,255,0.7)" }}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          variant="contained" 
          disabled={loading || !agreedToTerms || !stripe || !elements}
          sx={{ backgroundColor: "#3b82f6" }}
        >
          {loading ? <CircularProgress size={24} /> : "Subscribe Now"}
        </Button>
      </DialogActions>
    </form>
  );
};

export default SubscriptionFormDirect; 